/** @jsxImportSource @emotion/react */
import {
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text as TextChakraUI,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { SetterOrUpdater } from "recoil";
import EarthIcon from "../../public/new-email/EarthIcon";
import GroupIcon from "../../public/new-email/GroupIcon";
import TimeIcon from "../../public/new-email/TimeIcon";
import { IActiveEmailAtom } from "../atoms/ActiveEmailAtom";
import { ZoomAtomValue } from "../atoms/ZoomAtom";
import useClickOutside from "../hook/useClickOutside";
import { localStorageSetItem } from "../utils/LocalStorage";
import { WIDTH_ZOOM } from "./EmailEditor";

const PopoverButton = styled.div`
  width: 100%;
  height: 40px;
  padding: 22px 26px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #333;
  font-size: 16px;

  > svg {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    color: #bbb;
  }

  &:hover {
    background: #fb972e;
    color: white !important;
    svg {
      fill: white !important;
    }
  }

  &.active {
    font-weight: 600;
    color: #fb972e;

    &:hover {
      svg {
        filter: brightness(10000%);
      }
    }
  }

  &.attention {
    color: #c41700;
    &:hover {
      background: #c41700;
      color: white;
    }
  }
`;

interface SplitModePopoverProps {
  zoom: ZoomAtomValue;
  newEmailSplitMode: IActiveEmailAtom["splitMode"];
  setNewEmail: SetterOrUpdater<IActiveEmailAtom>;
}

const SplitModePopover = ({
  zoom,
  newEmailSplitMode,
  setNewEmail,
}: SplitModePopoverProps) => {
  const initialFocusRef = React.useRef();
  const [timeIconColor, setTimeIconColor] = useState("#bbb");
  const [earthIconColor, setEarthIconColor] = useState("#bbb");
  const [groupIconColor, setGroupIconColor] = useState("#bbb");

  return (
    <Popover
      initialFocusRef={initialFocusRef}
      closeOnBlur={false}
      placement="bottom-start"
      offset={newEmailSplitMode ? [0, -12] : [0, 5]}
    >
      {({ onClose }) => (
        <div
          ref={useClickOutside(() => {
            onClose();
          })}
        >
          <PopoverTrigger>
            <Flex
              width={WIDTH_ZOOM["default"][zoom.name]}
              cursor="pointer"
              css={css`
                img {
                  width: ${WIDTH_ZOOM["default"][zoom.name]};
                }
              `}
            >
              {!newEmailSplitMode ? (
                <img src="/new-email/ABIcon.svg" alt="AB" />
              ) : newEmailSplitMode === "AGE" ? (
                <img src="/new-email/isOpenTime.svg" alt="AGE" />
              ) : newEmailSplitMode === "DOMAIN" ? (
                <img src="/new-email/isOpenEarth.svg" alt="DOMAIN" />
              ) : (
                newEmailSplitMode === "AGE_DOMAIN" && (
                  <img src="/new-email/isOpenGroup.svg" alt="AGE_DOMAIN" />
                )
              )}
            </Flex>
          </PopoverTrigger>

          <PopoverContent
            _focus={{ outline: "none" }}
            overflow="hidden"
            width={"fit-content"}
          >
            <PopoverHeader px="16px">
              <TextChakraUI
                fontSize="13px"
                textTransform="uppercase"
                color="#888888"
                m={"6px 10px 0px 10px"}
              >
                Split By
              </TextChakraUI>
            </PopoverHeader>
            <PopoverBody p="0">
              <PopoverButton
                onClick={() => {
                  setNewEmail((ne) => ({
                    ...ne,
                    splitMode: "AGE",
                  }));
                  localStorageSetItem("DRAFT_EMAIL_SPLIT_MODE", "AGE");
                  onClose();
                }}
                className={newEmailSplitMode === "AGE" && "active"}
                onMouseEnter={() => setTimeIconColor("#ffffff")}
                onMouseLeave={() => setTimeIconColor("#bbb")}
              >
                <TimeIcon
                  color={
                    newEmailSplitMode === "AGE" ? "#fb972e" : timeIconColor
                  }
                />
                Account Age
              </PopoverButton>

              <PopoverButton
                onClick={() => {
                  setNewEmail((ne) => ({
                    ...ne,
                    splitMode: "DOMAIN",
                  }));
                  localStorageSetItem("DRAFT_EMAIL_SPLIT_MODE", "DOMAIN");
                  onClose();
                }}
                className={newEmailSplitMode === "DOMAIN" && "active"}
                onMouseEnter={() => setEarthIconColor("#ffffff")}
                onMouseLeave={() => setEarthIconColor("#bbb")}
              >
                <EarthIcon
                  color={
                    newEmailSplitMode === "DOMAIN" ? "#fb972e" : earthIconColor
                  }
                />
                Email Domain
              </PopoverButton>

              <PopoverButton
                onClick={() => {
                  setNewEmail((ne) => ({
                    ...ne,
                    splitMode: "AGE_DOMAIN",
                  }));
                  localStorageSetItem("DRAFT_EMAIL_SPLIT_MODE", "AGE_DOMAIN");
                  onClose();
                }}
                className={newEmailSplitMode === "AGE_DOMAIN" && "active"}
                onMouseEnter={() => setGroupIconColor("#ffffff")}
                onMouseLeave={() => setGroupIconColor("#bbb")}
              >
                <GroupIcon
                  color={
                    newEmailSplitMode === "AGE_DOMAIN"
                      ? "#fb972e"
                      : groupIconColor
                  }
                />
                Smart split using both
              </PopoverButton>
            </PopoverBody>
            <Divider m={0} />
            {newEmailSplitMode && (
              <PopoverFooter p="0">
                <PopoverButton
                  className="attention"
                  onClick={() => {
                    setNewEmail((ne) => ({
                      ...ne,
                      splitMode: undefined,
                    }));
                    localStorageSetItem("DRAFT_EMAIL_SPLIT_MODE", "");
                    onClose();
                  }}
                >
                  Remove split testing
                </PopoverButton>
              </PopoverFooter>
            )}
          </PopoverContent>
        </div>
      )}
    </Popover>
  );
};

export default SplitModePopover;
